import { useEffect, useRef, useState } from 'react';
import type { SelectedStyle } from '../../../components/image-generation';
import imageGenerationService from '../../../services/api/imageGenerationService';
import {
  fetchAutoStyles,
  getDiverseStyleCandidateLimit,
  pickRandomDistinctStyles,
  type RecommendedStyle,
} from '../../../services/api/autoStyleService';
import type { BannerStyleMode } from './BannerStyleChoice';

/**
 * Adapt an auto-recommended style into the SelectedStyle shape used by the
 * product-mode style allocation loop. cta/feature are unknown for recommended
 * styles, so server-side prompt generation falls back to the style template.
 */
export const recommendedStyleToSelected = (style: RecommendedStyle): SelectedStyle => ({
  id: style.id,
  name: style.name,
  stylePrompt: style.prompt_template ?? '',
  aspectRatio: '',
  category: '',
  minMaterialCount: style.min_material_count,
  maxMaterialCount: style.max_material_count,
  exampleImageUrl: style.thumbnail_url,
});

/**
 * 枚数の変更で選び直さず、足りない分だけ候補から足す／多い分は後ろから外す。
 * 見えているスタイルが枚数を変えただけで入れ替わると、何が選ばれたのか追えなくなるため。
 */
export function fitStylesToCount<T extends { id: number }>(current: T[], pool: T[], count: number): T[] {
  const target = Math.max(0, count);
  if (current.length >= target) return current.slice(0, target);
  const used = new Set(current.map((style) => style.id));
  const extra = pickRandomDistinctStyles(pool.filter((style) => !used.has(style.id)), target - current.length);
  return [...current, ...extra];
}

/** 一覧 API は prompt_template を返さないので、生成直前に足りないものだけ個別取得する */
export async function resolveStylePrompts(styles: SelectedStyle[]): Promise<SelectedStyle[]> {
  return Promise.all(styles.map(async (style) => {
    if (style.stylePrompt) return style;
    try {
      return { ...style, stylePrompt: await imageGenerationService.getStylePrompt(style.id) };
    } catch {
      return style;
    }
  }));
}

export const loadAllStyles = async (): Promise<SelectedStyle[]> => {
  const response = await imageGenerationService.getStyles();
  if (!response.success) return [];
  return response.data
    .filter((style) => style.is_active !== false)
    .map((style) => ({
      id: style.id,
      name: style.name,
      stylePrompt: style.style_prompt ?? '',
      aspectRatio: style.aspect_ratio,
      category: style.category,
      platforms: style.platforms,
      cta: style.cta,
      feature: style.feature,
      minMaterialCount: style.min_material_count,
      maxMaterialCount: style.max_material_count,
      backgroundTone: style.background_tone ?? null,
      exampleImageUrl: style.example_image_url ?? null,
    }));
};

export interface BannerStylePreviewState {
  /** 生成に使うスタイル（auto / none のとき）。manual では空 */
  styles: SelectedStyle[];
  loading: boolean;
  /**
   * auto モードで候補が1件も無かったときだけ入る、具体的な理由
   * （不足している素材の役割・実寸・縦横比など）。生成ボタンを押す前に
   * ユーザーへ表示できる。
   */
  noEligibleStyleReason: string | null;
  /** 商品情報を URL から取得中。候補は決めず、取得が終わるまで取り直す */
  productFetching: boolean;
}

/** 商品情報の取得中に auto-select を取り直す間隔 */
const PRODUCT_FETCH_RETRY_MS = 15000;

/**
 * 自動で使うスタイルを、生成前に選んでおく。
 * - auto: 商品に合う候補（/images/styles/auto-select）から枚数分
 * - none: 登録済みの全スタイルから枚数分をランダムに（フリーテキストの自動）
 */
export function useBannerStylePreview({ mode, productId, imageCount, inputText }: {
  mode: BannerStyleMode;
  productId: number | null;
  imageCount: number;
  inputText?: string;
}): BannerStylePreviewState {
  const [pool, setPool] = useState<SelectedStyle[] | null>(null);
  const [styles, setStyles] = useState<SelectedStyle[]>([]);
  const [loading, setLoading] = useState(false);
  const [noEligibleStyleReason, setNoEligibleStyleReason] = useState<string | null>(null);
  const [productFetching, setProductFetching] = useState(false);
  const requestRef = useRef(0);
  const inputTextRef = useRef(inputText);
  inputTextRef.current = inputText;

  const active = mode === 'none' || (mode === 'auto' && productId !== null);
  // auto は枚数に応じて候補数を広げる（少ない候補だと毎回同じ顔ぶれになる）
  const autoLimit = getDiverseStyleCandidateLimit(imageCount);
  const poolKey = !active ? null : mode === 'auto' ? `auto:${productId}:${autoLimit}` : 'none';

  useEffect(() => {
    const requestId = ++requestRef.current;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    setProductFetching(false);
    if (!poolKey) {
      setPool(null);
      setStyles([]);
      setNoEligibleStyleReason(null);
      setLoading(false);
      return undefined;
    }
    const run = () => {
      setLoading(true);
      const load: Promise<{ loaded: SelectedStyle[]; reason: string | null; fetching: boolean }> = poolKey === 'none'
        ? loadAllStyles().then((loaded) => ({ loaded, reason: null, fetching: false }))
        : fetchAutoStyles({
          productId: productId ?? undefined,
          inputText: inputTextRef.current || undefined,
          limit: autoLimit,
        }).then((response) => ({
          loaded: (response?.recommended ?? []).map(recommendedStyleToSelected),
          // 候補が1件もないときだけ、不足している素材と理由をそのまま見せる。
          reason: response?.recommended?.length ? null : response?.no_eligible_style_reason ?? null,
          fetching: Boolean(response?.product_fetching),
        }));
      load
        .catch(() => ({ loaded: [] as SelectedStyle[], reason: null, fetching: false }))
        .then(({ loaded, reason, fetching }) => {
          if (requestId !== requestRef.current) return;
          setProductFetching(fetching);
          if (fetching) {
            // 取得前の情報で選ぶと候補が減る（特典・画像がそろっていない）。終わるまで待って取り直す
            setPool(null);
            setStyles([]);
            setNoEligibleStyleReason(null);
            setLoading(false);
            retryTimer = setTimeout(run, PRODUCT_FETCH_RETRY_MS);
            return;
          }
          setPool(loaded);
          setStyles(pickRandomDistinctStyles(loaded, imageCount));
          setNoEligibleStyleReason(reason);
          setLoading(false);
        });
    };
    run();
    return () => {
      // 応答待ちのまま閉じた場合も、後から取り直しを仕掛けないように無効にする
      requestRef.current += 1;
      if (retryTimer) clearTimeout(retryTimer);
    };
    // imageCount は下の effect で合わせる（枚数の変更だけで選び直さない）
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poolKey]);

  useEffect(() => {
    if (!pool) return;
    setStyles((current) => fitStylesToCount(current, pool, imageCount));
  }, [pool, imageCount]);

  return { styles, loading, noEligibleStyleReason, productFetching };
}
